/**
 * URL-owned People list state. The roster and Refine controls land in later
 * slices; keeping their serialisation here means a refresh or browser history
 * navigation never loses the state those controls will consume.
 */
export interface PeopleQueryState {
  query: string;
  quickView?: string;
  relationshipStages: string[];
  sillStatuses: string[];
  verificationTiers: string[];
  timelines: string[];
  tags: string[];
  role?: string;
}

export const PEOPLE_QUICK_VIEWS = ["all", "active", "needs_me", "ready_soon", "going_cold"] as const;
export const PEOPLE_TIMELINES = ["0-3 months", "3-6 months", "6-12 months", "12+ months"] as const;
const TIMELINE_ALIASES: Record<string, string> = { "0–3 months": "0-3 months", "3–6 months": "3-6 months", "6–12 months": "6-12 months", "12+ months": "12+ months" };
export function canonicalPeopleTimeline(value: string): string { return TIMELINE_ALIASES[value.trim()] ?? value.trim(); }
export type PeopleQuickView = (typeof PEOPLE_QUICK_VIEWS)[number] | "archived";

const PARAMS = {
  query: "q",
  quickView: "view",
  relationshipStage: "stage",
  sillStatus: "status",
  verification: "verification",
  timeline: "timeline",
  tags: "tag",
  role: "role",
} as const;

export function peopleQueryFromSearchParams(
  searchParams: URLSearchParams,
): PeopleQueryState {
  return {
    query: searchParams.get(PARAMS.query) ?? "",
    quickView: searchParams.get(PARAMS.quickView) ?? undefined,
    relationshipStages: searchParams
      .getAll(PARAMS.relationshipStage)
      .filter(Boolean),
    sillStatuses: searchParams.getAll(PARAMS.sillStatus).filter(Boolean),
    verificationTiers: searchParams.getAll(PARAMS.verification).filter(Boolean),
    timelines: searchParams.getAll(PARAMS.timeline).filter(Boolean),
    tags: searchParams.getAll(PARAMS.tags).filter(Boolean),
    role: searchParams.get(PARAMS.role) ?? undefined,
  };
}

/**
 * Translate the compact browser vocabulary to the People API contract. This
 * deliberate boundary prevents UI names (`q`, `view`, `stage`) from becoming
 * accidental wire coupling, and preserves repeated values for multi-select
 * Refine controls. Archived is represented by `quick_view=archived`.
 */
export function peopleAPIQueryFromState(
  state: PeopleQueryState,
): URLSearchParams {
  const params = new URLSearchParams();
  if (state.query) params.set("query", state.query);
  if (state.quickView) params.set("quick_view", state.quickView);
  for (const value of state.relationshipStages) {
    params.append("relationship_stage", value);
  }
  for (const value of state.sillStatuses) params.append("sill_status", value);
  for (const value of state.verificationTiers) {
    params.append("verification_tier", value);
  }
  for (const value of state.timelines) params.append("timeline", canonicalPeopleTimeline(value));
  for (const value of state.tags) params.append("tag", value);
  if (state.role) params.set("role", state.role);
  return params;
}

/** Canonical filter fingerprint used to reject stale invite-freeze responses. */
export function peopleFilterFingerprint(state: PeopleQueryState): string {
  const sorted = (values: string[]) => [...new Set(values.map((v) => v.trim()).filter(Boolean))].sort();
  const params = new URLSearchParams();
  params.set("quick_view", state.quickView?.trim() || "all");
  params.set("sort", "attention");
  if (state.query.trim()) params.set("query", state.query.trim());
  for (const [key, values] of Object.entries({ relationship_stage: state.relationshipStages, sill_status: state.sillStatuses, verification_tier: state.verificationTiers, timeline: state.timelines, tag: state.tags })) {
    for (const value of sorted(values)) params.append(key, value);
  }
  if (state.role?.trim()) params.set("role", state.role.trim());
  return params.toString();
}

/** Changes one value while retaining future filter state already in the URL. */
export function withPeopleQueryValue(
  searchParams: URLSearchParams,
  key: keyof typeof PARAMS,
  value: string | undefined,
): URLSearchParams {
  const next = new URLSearchParams(searchParams);
  next.delete(PARAMS[key]);
  if (value?.trim()) next.set(PARAMS[key], value.trim());
  return next;
}
